import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Tabs,
    Tab,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import api from '../services/api';

// =============================================================================
// INTERFACES
// =============================================================================
interface EmailGeneratorProps {
    open: boolean;
    onClose: () => void;
    guestId: number;
    guestName: string;
}

interface GuestImage {
    id: number;
    image_url: string;
    caption?: string;
    display_order?: number;
}

interface ThankYouNote {
    id: number;
    guest_id: number;
    title: string;
    note_text?: string;
    gift?: string;
    created_at: string;
    updated_at: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// =============================================================================
// COMPONENTS
// =============================================================================
function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`email-tabpanel-${index}`}
            aria-labelledby={`email-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `email-tab-${index}`,
        'aria-controls': `email-tabpanel-${index}`,
    };
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export const EmailGenerator: React.FC<EmailGeneratorProps> = ({
    open,
    onClose,
    guestId,
    guestName
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [guestImages, setGuestImages] = useState<GuestImage[]>([]);
    const [thankYouNote, setThankYouNote] = useState<ThankYouNote | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [htmlCode, setHtmlCode] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Hardcoded main couple image and honeymoon images
    const mainCoupleImage = 'https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/photographer/0588+CHAB0236-Edit.jpg';
    const honeymoonImages = [
        'https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/honeymoon/IMG_7314.jpeg',
        'https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/honeymoon/IMG_7151.jpeg',
        'https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/honeymoon/IMG_0468.jpeg',
        'https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/honeymoon/IMG_7205.jpeg',
        'https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/honeymoon/IMG_7267.jpeg',
        'https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/honeymoon/IMG_7214.jpeg'
    ];

    useEffect(() => {
        if (open && guestId) {
            fetchGuestImages();
            fetchThankYouNote();
        }
    }, [open, guestId]);

    useEffect(() => {
        if (guestImages.length >= 0) { // Generate even if no images
            generateEmailHTML();
        }
    }, [guestImages, thankYouNote, guestName]);

    const fetchGuestImages = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const params = new URLSearchParams({
                'filter[guest_id]': guestId.toString()
            });
            
            const response = await api.get(`/guest_images?${params}`);
            const sortedImages = response.data.sort((a: GuestImage, b: GuestImage) => 
                (a.display_order || 0) - (b.display_order || 0)
            );
            setGuestImages(sortedImages);
        } catch (err) {
            console.error('Error fetching guest images:', err);
            setError('Failed to load guest images');
        } finally {
            setLoading(false);
        }
    };

    const fetchThankYouNote = async () => {
        try {
            const params = new URLSearchParams({
                'filter[guest_id]': guestId.toString()
            });
            
            const response = await api.get(`/thank_you_notes?${params}`);
            if (response.data && response.data.length > 0) {
                setThankYouNote(response.data[0]); // Get the first (and likely only) thank you note for this guest
            }
        } catch (err) {
            console.error('Error fetching thank you note:', err);
            // Don't set error for thank you notes - it's optional
        }
    };

    const generateEmailHTML = () => {
        const guestImagesHTML = guestImages.length > 0 ? generateGuestImagesHTML() : '';
        
        // Dynamic thank you message - use custom note_text if available, otherwise use default
        const thankYouMessage = thankYouNote?.note_text || 
            "We wanted to take a moment to say thank you from the bottom of our hearts. Your presence at our wedding meant the world to us, and we're so grateful to have shared such a special day with you.";
        
        // Dynamic gift message - use custom gift text if available, otherwise use default
        const giftMessage = thankYouNote?.gift || 
            "We also wanted to thank you for your kind and generous gift. Your contribution helped us pay for our honeymoon, and we made some unforgettable memories!";
        
        const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Thank You</title>
</head>
<body style="font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #ffffff;">
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: auto;">
    <tr>
      <td style="padding: 20px;">

        <!-- Greeting -->
        <p style="font-size: 16px; color: #333;">Dear ${guestName},</p>
        <p style="font-size: 16px; color: #333;">
          ${thankYouMessage}
        </p>

        <!-- Main wedding photo -->
        <table width="100%" cellpadding="0" cellspacing="0" style="margin: 20px 0;">
          <tr>
            <td>
              <img src="${mainCoupleImage}" alt="Our wedding" style="width: 100%; display: block;">
            </td>
          </tr>
        </table>

        <!-- Two photos side-by-side -->
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td width="50%" style="padding-right: 5px;">
              <img src="https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/photographer/0966 CHR63624.jpg" alt="Cake" style="width: 100%; display: block;">
            </td>
            <td width="50%" style="padding-left: 5px;">
              <img src="https://wrkflowpro-wedding.s3.eu-west-2.amazonaws.com/photographer/0537 CHR62922.jpg" alt="Confetti" style="width: 100%; display: block;">
            </td>
          </tr>
        </table>

        <!-- Guest moments -->
        ${guestImagesHTML}

        <!-- Thank You for the Gift -->
        <p style="font-size: 16px; color: #333; margin-top: 30px;">
          ${giftMessage}
        </p>

        <!-- Honeymoon gallery -->


        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td width="33%" style="padding: 5px;">
              <img src="${honeymoonImages[0]}" alt="" style="width: 100%; display: block;">
            </td>
            <td width="33%" style="padding: 5px;">
              <img src="${honeymoonImages[1]}" alt="" style="width: 100%; display: block;">
            </td>
            <td width="33%" style="padding: 5px;">
              <img src="${honeymoonImages[2]}" alt="" style="width: 100%; display: block;">
            </td>
          </tr>
          <tr>
            <td width="33%" style="padding: 5px;">
              <img src="${honeymoonImages[3]}" alt="" style="width: 100%; display: block;">
            </td>
            <td width="33%" style="padding: 5px;">
              <img src="${honeymoonImages[4]}" alt="" style="width: 100%; display: block;">
            </td>
            <td width="33%" style="padding: 5px;">
              <img src="${honeymoonImages[5]}" alt="" style="width: 100%; display: block;">
            </td>
          </tr>
        </table>

        <!-- Call to reply -->
        <p style="font-size: 16px; color: #333; margin-top: 30px;">
          Thank you again for your love, laughter, and for making our day truly unforgettable.
        </p>

        <!-- Closing -->
        <p style="font-size: 16px; color: #333;">
          With love,<br>
          <strong>Steve & Sarah</strong>
        </p>

        <!-- Optional links -->
        <p style="font-size: 14px; color: #666; margin-top: 30px;">
          <a href="https://sabiandsaint.pic-time.com/-sarahsteven/gallery?inviteptoken2=AAAAAAMBAAB7UqPfFhxtcJgXvh5rWJHReQ%2C%2C" style="color: #1a73e8;">View photo gallery</a> | 
          <a href="https://vimeo.com/1093930857/aa1c2f0fcf" style="color: #1a73e8;">Watch video highlights</a>
        </p>

      </td>
    </tr>
  </table>
</body>
</html>`;

        setHtmlCode(html);
    };

    const generateGuestImagesHTML = () => {
        if (guestImages.length === 0) return '';

        // Create rows of 3 images each
        const rows = [];
        for (let i = 0; i < guestImages.length; i += 3) {
            const rowImages = guestImages.slice(i, i + 3);
            const cells = rowImages.map(image => 
                `<td width="33%" style="padding: 5px;">
                   <img src="${image.image_url}" alt="${image.caption || 'You at the wedding'}" style="width: 100%; display: block;">
                 </td>`
            ).join('');
            
            // Fill empty cells if needed
            const emptyCells = (3 - rowImages.length);
            const emptyHtml = '<td width="33%" style="padding: 5px;"></td>'.repeat(emptyCells);
            
            rows.push(`<tr>${cells}${emptyHtml}</tr>`);
        }

        return `
        <!-- Favorite moments -->
        <p style="font-size: 16px; color: #333; margin-top: 30px;">
          Here are a few of our favorite moments with you:
        </p>

        <table width="100%" cellpadding="0" cellspacing="0">
          ${rows.join('')}
        </table>`;
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(htmlCode);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    const handleClose = () => {
        setTabValue(0);
        setGuestImages([]);
        setThankYouNote(null);
        setHtmlCode('');
        setError(null);
        setCopySuccess(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { height: '90vh' }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon />
                    <Typography variant="h6">
                        Email Generator - {guestName}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading guest images...</Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && (
                    <>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={handleTabChange} aria-label="email tabs">
                                <Tab label="HTML Code" {...a11yProps(0)} />
                                <Tab label="Preview" {...a11yProps(1)} />
                            </Tabs>
                        </Box>

                        <TabPanel value={tabValue} index={0}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">HTML Code</Typography>
                                <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<CopyIcon />}
                                        onClick={handleCopyToClipboard}
                                        color={copySuccess ? "success" : "primary"}
                                    >
                                        {copySuccess ? "Copied!" : "Copy"}
                                    </Button>
                                </Tooltip>
                            </Box>
                            <TextField
                                multiline
                                fullWidth
                                value={htmlCode}
                                onChange={(e) => setHtmlCode(e.target.value)}
                                rows={20}
                                variant="outlined"
                                sx={{ 
                                    fontFamily: 'monospace',
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'monospace',
                                        fontSize: '12px'
                                    }
                                }}
                                placeholder="HTML code will appear here..."
                            />
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Email Preview</Typography>
                            <Box
                                sx={{
                                    border: '1px solid #ddd',
                                    borderRadius: 1,
                                    height: '500px',
                                    overflow: 'auto',
                                    backgroundColor: '#f4f4f4',
                                    p: 2
                                }}
                            >
                                <div dangerouslySetInnerHTML={{ __html: htmlCode }} />
                            </Box>
                        </TabPanel>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose}>Close</Button>
                <Button
                    variant="contained"
                    onClick={handleCopyToClipboard}
                    startIcon={<CopyIcon />}
                    disabled={!htmlCode}
                >
                    Copy HTML
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmailGenerator; 